/**
 * THE UPSCALE VOCABULARY — the mode names, the parser and the ≥1px clamp. PURE.
 *
 * Split out of `pipeline.ts` for the same reason `src/net/axon/gate.ts` is split out of the
 * gateway: the tuning schema and the tests need the NAMES, and `pipeline.ts` imports
 * `three/webgpu` plus three example post nodes. A zod schema that dragged the whole node system
 * into every headless test run would be paying for a shader compiler to validate a string.
 *
 * Nothing here knows what a renderer is. The strategy table itself — what each mode BUILDS —
 * lives in `pipeline.ts`, and the design rationale is in that file's header.
 */

/**
 * Ordered cheapest first.
 *
 * - `bilinear`    — no `RenderPipeline` at all: the scene draws straight to a small drawing
 *                   buffer and the browser compositor magnifies it. Zero extra passes, identical
 *                   on both backends. Soft, and the honest floor.
 * - `sharpen`     — RCAS 1:1 at RENDER resolution, then the same compositor magnification.
 *                   Two extra passes, both at the CHEAP resolution.
 * - `sharpen-out` — DIAGNOSTIC ONLY. RCAS at OUTPUT resolution over a low-res pass, which is a
 *                   nearest-neighbour magnify (`SharpenNode` fetches with `textureLoad` at the
 *                   INPUT texture's size). Kept so that claim stays a measurement.
 * - `fsr1`        — EASU + RCAS, both at OUTPUT resolution.
 * - `taau`        — temporal upsample: jittered camera, velocity MRT, history reprojection.
 */
export type UpscaleMode = 'bilinear' | 'sharpen' | 'sharpen-out' | 'fsr1' | 'taau';

export const UPSCALE_MODES = [
  'bilinear',
  'sharpen',
  'sharpen-out',
  'fsr1',
  'taau',
] as const satisfies readonly UpscaleMode[];

/** Parse a raw `?upscale=` value. Anything unrecognised → null (no override). */
export function parseUpscaleMode(value: string | null | undefined): UpscaleMode | null {
  if (value === null || value === undefined || value === '') return null;
  const v = value.toLowerCase();
  if (v === 'off' || v === 'none') return 'bilinear';
  return (UPSCALE_MODES as readonly string[]).includes(v) ? (v as UpscaleMode) : null;
}

/**
 * The ≥1px clamp on the scene pass's resolution scale, in the one place it can be right.
 *
 * `PassNode.setSize` does a bare `Math.floor(width × scale)` with NO lower bound, so a 1px-tall
 * host (a collapsed flex parent mid-layout) at scale 0.5 produces a zero-height render target —
 * the same class of bug as the zero-instance `InstancedMesh` this repo already shipped, and
 * exactly what the `@webgpu` gate exists to catch.
 */
export function clampPassScale(scale: number, bufferW: number, bufferH: number): number {
  const w = Math.max(1, Math.floor(bufferW));
  const h = Math.max(1, Math.floor(bufferH));
  const safe = Number.isFinite(scale) && scale > 0 ? Math.min(scale, 1) : 1;
  return Math.max(safe, 1 / w, 1 / h);
}
